import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import {
  ADMINISTRATOR_GROUP_NAME,
  containsAdminGroup,
  IProjectGroup,
  removeOrAddAdminGroup,
} from '@core/state/entities/project-group/project-group.entity';
import { Action, ActionCreator, Creator, Store } from '@ngrx/store';
import { firstValueFrom, map, Observable } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import {
  IInfoConfirmationDialogData,
  InfoConfirmationDialogComponent,
} from '@shared/dialogs/info-confirmation-dialog/info-confirmation-dialog.component';
import { throwErrorExp } from '@core/actions/util';
import {
  IErrorStateWithCorrelation,
  IProjectAccessState,
  IRequestCorrelationState,
} from '@core/actions/project-detail.actions';
import { v4 as uuid } from 'uuid';
import { UpperCasePipe } from '@angular/common';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { InformationBoxComponent } from '../../../../shared/information-box/information-box.component';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
export interface IActivityInfo<
  TSuccessActionProps extends IRequestCorrelationState =
    IRequestCorrelationState,
  TNegativeActionProps extends IErrorStateWithCorrelation =
    IErrorStateWithCorrelation,
  TDispatchedActionProps extends IRequestCorrelationState =
    IRequestCorrelationState,
> {
  infoBoxMessage?: string;
  buttonText: string;
  dispatchAction: ({
    newProjectGroupIds,
    message,
    correlationId,
  }: {
    newProjectGroupIds?: string[];
    message?: string;
    correlationId: string;
  }) => Action & TDispatchedActionProps;
  successActions:
    | ActionCreator<string, Creator<any[], TSuccessActionProps>>
    | ActionCreator<string, Creator<any[], TSuccessActionProps>>[];
  errorActions:
    | ActionCreator<string, Creator<any[], TNegativeActionProps>>
    | ActionCreator<string, Creator<any[], TNegativeActionProps>>[];
  independentOfProjectGroupChange?: boolean;
  confirmationDialog?: (isAdmin: boolean) => IInfoConfirmationDialogData | null;
  successMessage?: string;
  friendlyErrorMessage?: string;
}
export interface IEditEntityDialogData {
  isOwner?: boolean;
  isCurrent?: boolean;
  dialogIcon: string;
  entityLabel: string;
  name: string;
  message?: string;
  description?: string;
  projectGroups: IProjectGroup[];
  originalGroups?: IProjectGroup[];
  dismissButtonText: string;
  primaryActivity: IActivityInfo;
  negativeActivity?: IActivityInfo;
  secondaryActivity?: IActivityInfo;
  projectGroupHint?: string;
}

@Component({
  selector: 'app-edit-entity-dialog',
  templateUrl: './edit-entity-dialog.component.html',
  styleUrls: ['./edit-entity-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatProgressBarModule,
    MatDialogTitle,
    MatIconModule,
    MatButtonModule,
    MatDialogContent,
    FlexLayoutModule,
    InformationBoxComponent,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatRadioModule,
    MatDialogActions,
    MatDialogClose,
    UpperCasePipe,
  ],
})
export class EditEntityDialogComponent {
  inProgress = false;
  secondaryMessage = '';
  get isAdminGroupAssigned(): boolean {
    return containsAdminGroup(this.data.originalGroups ?? []);
  }
  constructor(
    private _action: Actions,
    private _dialog: MatDialog,
    private _store: Store,
    @Inject(MAT_DIALOG_DATA) public data: IEditEntityDialogData,
    private _dialogRef: MatDialogRef<EditEntityDialogComponent>
  ) {}
  getNewProjectGroups(shouldIncludeAdmin: boolean): string[] {
    return removeOrAddAdminGroup(
      this.data.originalGroups ?? [],
      shouldIncludeAdmin,
      this.data.projectGroups
    ).map((g) => g.id);
  }
  async action(
    activityInfo: IActivityInfo,
    newProjectGroupIds: string[],
    message?: string
  ): Promise<void> {
    const isAdmin = newProjectGroupIds.includes(
      this.data.projectGroups.find((g) => g.name === ADMINISTRATOR_GROUP_NAME)
        ?.id ?? throwErrorExp('Admin group not found')
    );
    const confirmationDialog =
      activityInfo.confirmationDialog?.(isAdmin) ?? null;
    if (
      confirmationDialog &&
      !(await firstValueFrom(
        this._dialog
          .open<
            InfoConfirmationDialogComponent,
            IInfoConfirmationDialogData,
            boolean
          >(InfoConfirmationDialogComponent, {
            data: confirmationDialog,
          })
          .afterClosed()
      ))
    ) {
      return;
    }
    this.inProgress = true;
    this._store.dispatch(
      activityInfo.dispatchAction({
        newProjectGroupIds,
        message: this.secondaryMessage,
        correlationId: uuid(),
      })
    );
    await firstValueFrom(
      this._action.pipe(
        ofType(
          ...[activityInfo.successActions, activityInfo.errorActions].flat(2)
        ),
        map((action) => void 0)
      )
    );
    this.inProgress = false;
    this._dialogRef.close(true);
  }
}
